import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Mail, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/api";
import { useLang } from "@/i18n";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";

const EMPTY = { name: "", email: "", subject: "", message: "", privacy_consent: false };

export default function Contact() {
    const { lang, t } = useLang();
    const [params] = useSearchParams();
    const [form, setForm] = useState(EMPTY);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [settings, setSettings] = useState(null);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    useEffect(() => {
        api.get("/public/settings").then((r) => setSettings(r.data)).catch(() => {});
    }, []);

    /* CTA "Richiedi questo transfer" dalla pagina /transfer: oggetto precompilato. */
    useEffect(() => {
        const servizio = params.get("servizio");
        if (servizio) set("subject", servizio);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const valid = form.name.length >= 2 && form.email.includes("@") && form.subject.length >= 2 && form.message.length >= 5 && form.privacy_consent;

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await api.post("/public/contact", form);
            setSent(true);
        } catch (err) {
            toast.error(apiError(err, t.booking.errorGeneric));
        } finally {
            setSending(false);
        }
    };

    const L = ({ children }) => <span className="text-[11px] uppercase tracking-widest text-amber-500/80">{children}</span>;

    return (
        <div data-testid="contact-page" className="marble-bg min-h-screen pt-32 pb-24">
            <Seo lang={lang} title={`${t.contact.title} — TOURVOYAGERS`} description={t.contact.sub} />
            <div className="max-w-6xl mx-auto px-5 sm:px-8 grid gap-14 lg:grid-cols-2">
                <Reveal>
                    <p className="text-xs uppercase tracking-[0.3em] text-amber-400/90 font-medium">TOURVOYAGERS</p>
                    <h1 className="mt-4 font-display text-4xl sm:text-5xl text-amber-50 leading-tight">{t.contact.title}</h1>
                    <p className="mt-4 text-base text-stone-300 leading-relaxed">{t.contact.sub}</p>

                    <div className="mt-10 gold-card rounded-xl p-8" data-testid="official-contacts">
                        <h2 className="font-display text-xl text-amber-100">{t.contact.officialTitle}</h2>
                        <ul className="mt-5 space-y-4 text-sm text-stone-300">
                            <li className="flex items-center gap-3">
                                <Mail size={16} className="shrink-0 text-amber-400" />
                                {settings?.email ? (
                                    <a href={`mailto:${settings.email}`} data-testid="contact-email-link"
                                        className="break-all transition-colors hover:text-amber-200 underline-offset-4 hover:underline">
                                        {settings.email}
                                    </a>
                                ) : "—"}
                            </li>
                            <li className="flex items-center gap-3">
                                <Phone size={16} className="shrink-0 text-amber-400" />
                                {settings?.phone ? (
                                    <a href={`tel:${settings.phone.replace(/[^+\d]/g, "")}`} data-testid="contact-phone-link"
                                        className="transition-colors hover:text-amber-200 underline-offset-4 hover:underline">
                                        {settings.phone}
                                    </a>
                                ) : "—"}
                            </li>
                            <li className="flex items-center gap-3">
                                <MapPin size={16} className="shrink-0 text-amber-400" />
                                {settings?.address ? (
                                    <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(settings.address)}`}
                                        target="_blank" rel="noopener noreferrer" data-testid="contact-address-link"
                                        className="transition-colors hover:text-amber-200 underline-offset-4 hover:underline">
                                        {settings.address}
                                    </a>
                                ) : "—"}
                            </li>
                        </ul>
                        {settings?.vat_number && <p className="mt-4 text-xs text-stone-500" data-testid="contact-vat">{settings.vat_number}</p>}
                        <div className="mt-6 border-t border-amber-500/10 pt-4 flex flex-col gap-1.5 text-xs text-stone-500">
                            <span>{t.footer.privacy}</span>
                            <span>{t.footer.cookie}</span>
                            <span>{t.footer.terms} · {t.footer.cancellationPolicy}</span>
                        </div>
                    </div>
                </Reveal>

                <Reveal delay={0.12}>
                    {sent ? (
                        <div className="gold-card rounded-xl p-10 text-center lg:mt-24" data-testid="contact-success">
                            <CheckCircle2 className="mx-auto text-amber-400" size={52} />
                            <h2 className="mt-4 font-display text-2xl text-amber-50">{t.contact.successTitle}</h2>
                            <p className="mt-3 text-sm text-stone-300">{t.contact.successMsg}</p>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="gold-card rounded-xl p-8 grid gap-4 lg:mt-24" data-testid="contact-form">
                            <label className="block"><L>{t.contact.name} *</L><input data-testid="contact-name" className="input-lux mt-1" value={form.name} onChange={(e) => set("name", e.target.value)} /></label>
                            <label className="block"><L>{t.contact.email} *</L><input data-testid="contact-email" type="email" className="input-lux mt-1" value={form.email} onChange={(e) => set("email", e.target.value)} /></label>
                            <label className="block"><L>{t.contact.subject} *</L><input data-testid="contact-subject" className="input-lux mt-1" value={form.subject} onChange={(e) => set("subject", e.target.value)} /></label>
                            <label className="block"><L>{t.contact.message} *</L><textarea data-testid="contact-message" rows={5} className="input-lux mt-1" value={form.message} onChange={(e) => set("message", e.target.value)} /></label>
                            <label className="flex items-start gap-3 text-xs text-stone-400 cursor-pointer">
                                <input data-testid="contact-privacy" type="checkbox" checked={form.privacy_consent} onChange={(e) => set("privacy_consent", e.target.checked)} className="mt-0.5 accent-amber-400" />
                                {t.contact.privacy}
                            </label>
                            <button data-testid="contact-submit" disabled={!valid || sending} className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 px-8 py-3.5 text-xs font-bold uppercase tracking-[0.16em] text-black transition-all hover:bg-amber-300 hover:shadow-[0_0_24px_rgba(212,175,55,0.45)] disabled:opacity-40 disabled:cursor-not-allowed">
                                {sending && <Loader2 size={14} className="animate-spin" />}
                                {t.contact.send}
                            </button>
                        </form>
                    )}
                </Reveal>
            </div>
        </div>
    );
}
